import { formatISO } from "date-fns";
import { FinancialDocumentListItemSchema } from "@common/schemas/financialDocument/financial-document-list-item.schema";
import { EagerLoaded } from "../../types/base/orm";
import {
    FinancialDocumentCounterparty,
    FinancialDocumentListItem,
    FinancialDocumentListItemAccrualExtractionStatus,
    FinancialDocumentListItemIntegrationAccount,
} from "../../types/financialDocument";
import { DocumentCategory } from "../../types/documentCategory";
import { Entity } from "../../types/entity";
import { FinancialAccount } from "../../types/financialAccount";
import { TransactionMatch } from "../../types/transactionMatch";
import { TransactionStatus, TransactionType } from "../../types/transaction";
import { User, UserIncomeType } from "../../types/user";
import { EntityDto } from "../entity/entity.dto";
import { FinancialAccountDto } from "../financialAccount/financial-account.dto";
import { TransactionMatchDto } from "../transactionMatch/transaction-match.dto";
import { TransactionMatchFinancialTransactionDto } from "../transactionMatch/transaction-match-financial-transaction.dto";
import { UserDto } from "../user/user.dto";
import { FinancialDocumentDto } from "./financial-document.dto";

type ListItemEntityWire = NonNullable<
    FinancialDocumentListItemSchema["entity"]
>;
type ListItemFinancialAccountWire = NonNullable<
    FinancialDocumentListItemSchema["financialAccount"]
>;
type ListItemUploaderWire = NonNullable<
    FinancialDocumentListItemSchema["uploader"]
>;
type ListItemTransactionMatchWire =
    FinancialDocumentListItemSchema["transactionMatches"][number];
type ListItemFinancialTransactionWire =
    ListItemTransactionMatchWire["financialTransaction"];

function listItemEntityWireToDto(wire: ListItemEntityWire): EntityDto {
    return {
        id: wire.id,
        name: wire.name,
        iconColor: wire.iconColor,
        logo: wire.logo,
        isBusiness: wire.isBusiness,
        isPersonal: wire.isPersonal,
        userId: wire.userId,
        workspaceId: wire.workspaceId,
        federalTaxRate: wire.federalTaxRate,
        industry: wire.industry,
        legalType: wire.legalType,
        uuid: "",
        address: null,
        bookkeepingYears: [],
        bookkeepingStartDate: "",
        taxLocations: [],
        onboardingComplete: false,
        user: null,
        profile: null,
        isMockEntity: false,
        openingBalanceSubmitted: false,
        reportsReady: false,
        transactionEnrichmentPausedAt: null,
        allowAITrainingOnTheirData: false,
        hasCustomChartOfAccounts: false,
    };
}

function listItemFinancialAccountWireToDto(
    wire: ListItemFinancialAccountWire,
): FinancialAccountDto {
    return {
        id: wire.id,
        name: wire.name,
        sourceType: wire.sourceType,
        accountNumberMask: wire.accountNumberMask,
        entityId: wire.entityId,
        institutionName: wire.institutionName,
        financialType: wire.financialType,
        balance: null,
        balanceSyncedAt: null,
        transactionsSyncedAt: null,
        continuousReconciliationEnabledAt: null,
        createdAt: null,
        entity: null,
        isBusiness: null,
        order: 0,
        routingNumber: null,
        wireRoutingNumber: null,
        disabled: false,
        bookkeepingStartDate: null,
        bookkeepingEndDate: null,
    };
}

function listItemUploaderWireToDto(wire: ListItemUploaderWire): UserDto {
    const [firstName, ...lastNameParts] = wire.name.split(" ");
    const wireEpochIso = formatISO(new Date(0));

    return {
        id: wire.id,
        name: wire.name,
        legalName: null,
        preferredName: null,
        email: wire.email,
        avatar: wire.avatar,
        firstName: firstName ?? "",
        lastName: lastNameParts.join(" "),
        phoneNumber: null,
        unverifiedPhoneNumber: null,
        phoneNumberDisplay: null,
        dateOfBirth: null,
        incomeType: [] satisfies UserIncomeType[],
        onboardingComplete: true,
        showBookkeepingReviewInvite: false,
        bookkeepingReviewScheduled: false,
        lastActivity: wireEpochIso,
        iconColor: wire.iconColor,
        savingsCreditLimit: 0,
        freeUsageLimit: 0,
        settings: {
            weeklyInsightsEnabledAt: null,
            monthlyInsightsEnabledAt: null,
            dailyTasksDigestEnabledAt: null,
        },
        createdAt: wireEpochIso,
        onboardingCompletedAt: null,
        eventsTriggered: null,
        stripeCustomerId: null,
        bookedEnterpriseCall: false,
        type: wire.type,
        signupPurpose: null,
    };
}

function listItemFinancialTransactionWireToDto(
    wire: ListItemFinancialTransactionWire,
): TransactionMatchFinancialTransactionDto {
    return {
        id: wire.id,
        amount: wire.amount,
        originalAmount: wire.amount,
        foreignCurrencyAmount: null,
        foreignCurrency: null,
        date: wire.date,
        dateOnly: null,
        type: TransactionType.IMPORTED,
        status: TransactionStatus.COMPLETED,
        bankDescription: wire.bankDescription,
        normalizedBankDescription: null,
        normalizedDescriptionId: null,
        displayDescription: wire.displayDescription,
        categoryId: wire.categoryId,
        businessCategoryId: null,
        personalCategoryId: null,
        categoryBackup: null,
        memo: null,
        amountSaved: null,
        potentialAmountSaved: null,
        requiredActions: [],
        taxQuestions: [],
        deductionRate: 0,
        requirementsSnoozed: false,
        isIgnored: false,
        isBusiness: true,
        inReview: false,
        accountId: null,
        accrualAccountId: null,
        suggestedAccrualAccountId: null,
        accountOverrides: [],
        entityId: wire.entityId,
        financialAccountId: wire.financialAccountId,
        counterpartyId: null,
        customerId: null,
        billableExpenseInvoiceId: null,
        splitParentId: null,
        categorizationSource: null,
        counterpartyIdSource: null,
    };
}

function listItemTransactionMatchWireToDto(
    wire: ListItemTransactionMatchWire,
): TransactionMatchDto {
    return {
        id: wire.id,
        financialTransaction: listItemFinancialTransactionWireToDto(
            wire.financialTransaction,
        ),
    };
}

export function deserializeFinancialDocumentListItemFromSchema(
    wire: FinancialDocumentListItemSchema,
): FinancialDocumentListItem {
    const {
        entity,
        financialAccount,
        transactionMatches,
        integrationAccount,
        uploader,
        documentCategory,
        counterparty,
        documentInvoicesExtraction,
        documentBillsExtraction,
        ...scalars
    } = wire;

    const base = FinancialDocumentDto.deserialize({
        ...scalars,
        loanExtraction: null,
    });

    const deserializedEntity: Entity | null = entity
        ? EntityDto.deserialize(listItemEntityWireToDto(entity))
        : null;
    const deserializedFinancialAccount: FinancialAccount | null =
        financialAccount
            ? FinancialAccountDto.deserialize(
                  listItemFinancialAccountWireToDto(financialAccount),
              )
            : null;
    const deserializedUploader: User | null = uploader
        ? UserDto.deserialize(listItemUploaderWireToDto(uploader))
        : null;
    const deserializedTransactionMatches = transactionMatches.map(
        (transactionMatch) =>
            TransactionMatchDto.deserialize(
                listItemTransactionMatchWireToDto(transactionMatch),
            ),
    ) as Array<EagerLoaded<TransactionMatch, "financialTransaction">>;
    const deserializedIntegrationAccount: FinancialDocumentListItemIntegrationAccount | null =
        integrationAccount;
    const deserializedDocumentCategory: DocumentCategory | null =
        documentCategory ?? null;
    const deserializedCounterparty: FinancialDocumentCounterparty | null =
        counterparty ?? null;
    const deserializedDocumentInvoicesExtraction: FinancialDocumentListItemAccrualExtractionStatus | null =
        documentInvoicesExtraction ?? null;
    const deserializedDocumentBillsExtraction: FinancialDocumentListItemAccrualExtractionStatus | null =
        documentBillsExtraction ?? null;

    return {
        ...base,
        entity: deserializedEntity,
        financialAccount: deserializedFinancialAccount,
        transactionMatches: deserializedTransactionMatches,
        integrationAccount: deserializedIntegrationAccount,
        uploader: deserializedUploader,
        documentCategory: deserializedDocumentCategory,
        counterparty: deserializedCounterparty,
        documentInvoicesExtraction: deserializedDocumentInvoicesExtraction,
        documentBillsExtraction: deserializedDocumentBillsExtraction,
    };
}
